import React, { useState } from 'react'
import { Box, CircularProgress } from '@mui/material'
import { useUpdateAndPublishProduct } from '../../TreeActions'
import { ConfirmWithDialogButton } from 'shared/ui'
import { MasterUpdate } from './MasterUpdate'
import { AccumulatorOptions } from './AccumulatorOptions'
import { useAtomValue } from 'infrastructure/state/jotai.wrapper'
import { ErrorDialog } from '../../../resources/shared/ErrorDialog'
import {
  containsValidationErrorsAtom,
  exceptionsAtom,
  selectedUpdatesAtom,
  gettingUpdatesAtom,
  updatingSelectedUpdatesAtom,
} from './atoms'
import { useAtom } from 'infrastructure/state/jotai.wrapper'
import { useUpdatesActions } from './utility'

export function CurriculaVersionUpdate() {
  const updates = useAtomValue(selectedUpdatesAtom)
  const [updating] = useAtom(updatingSelectedUpdatesAtom)
  const [exceptions, setExceptions] = useAtom(exceptionsAtom)
  const [errors, setErrors] = useState<string[]>([])

  return (
    <Box>
      <ProcessDescription />
      <Box style={{ padding: 12, border: 'solid 1px lightGrey' }}>
        <p>Updating {updates.length} Curricula</p>
        {updates.map(({ treeId }, index) => (
          <MasterUpdate
            key={treeId}
            disabled={updating}
            bypassValidations={exceptions.includes(treeId)}
            index={index}
            excuse={() => setExceptions((prev) => [...prev, treeId])}
            require={() => {
              setExceptions((prev) => prev.filter((id) => id !== treeId))
            }}
          />
        ))}
      </Box>
      <DoItButton />
      <ErrorDialog
        title="Update Errors"
        errors={errors}
        onClose={() => setErrors([])}
      />
      <Box style={{ padding: 12, border: 'solid 1px lightGrey' }}>
        <AccumulatorOptions />
      </Box>
    </Box>
  )
}

function DoItButton() {
  const [updating, setUpdating] = useAtom(updatingSelectedUpdatesAtom)
  const [currentMessage, setCurrentMessage] = useState('')
  const updates = useAtomValue(selectedUpdatesAtom)
  const updatesContainValidationErrors = useAtomValue(
    containsValidationErrorsAtom,
  )
  const gettingUpdates = useAtomValue(gettingUpdatesAtom)
  const updateProducts = useUpdateAndPublishProduct()
  const { refreshAllUpdates } = useUpdatesActions()

  const doIt = async () => {
    setUpdating(true)
    try {
      setCurrentMessage('Refreshing version data...')
      const freshUpdates = await refreshAllUpdates()
      const startTime = new Date().toLocaleTimeString()
      const label =
        freshUpdates.length === 1
          ? '1 curriculum'
          : `${freshUpdates.length} curricula`
      setCurrentMessage(`Updating ${label}... Started at ${startTime}`)
      const result = await updateProducts(freshUpdates)
      const endTime = new Date().toLocaleTimeString()
      if (result.errors.length) {
        setCurrentMessage(
          `Completed at ${endTime} with errors:\n${result.errors.join('\n')}`,
        )
      } else {
        setCurrentMessage(
          `${label} updated successfully. Completed at ${endTime}`,
        )
      }
    } catch (error) {
      setCurrentMessage(
        `An error occurred: ${error instanceof Error ? error.message : String(error)}`,
      )
    } finally {
      setUpdating(false)
    }
  }

  return (
    <Box>
      <ConfirmWithDialogButton
        dialogTitle={`Update and Publish ${updates.length} Courses`}
        disabled={
          updating ||
          gettingUpdates ||
          !updates.length ||
          updatesContainValidationErrors
        }
        onClick={doIt}
        dialogContent={
          <Box>
            You are updating the following curricula:
            <Box padding="5px">
              {updates.map(({ toTreeTitle, treeId }) => (
                <Box key={treeId}>{toTreeTitle}</Box>
              ))}
            </Box>
          </Box>
        }
      >
        Do It
      </ConfirmWithDialogButton>
      {currentMessage && (
        <Box
          sx={{
            mt: 1,
            p: 1,
            whiteSpace: 'pre-wrap',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          {updating && <CircularProgress size={20} />}
          {currentMessage}
        </Box>
      )}
    </Box>
  )
}

function ProcessDescription() {
  return (
    <Box width="80%" padding="5px">
      <b>Curricula Version Update</b>
      <p>
        Use this tool to create a new version for existing bundled curricula
      </p>
      <p>
        You are able to use existing trees or trees from local files. Either way
        a new tree is created for the version update.
      </p>
      <p>
        The tool will attempt to automatically find the curricula you are
        updating from based on the title. If it is not successful finding the
        existing version make sure what you have is correct. The version will be
        automatically incremented but can be overridden. If you are in the
        production environment you will see curricula with matching attribution
        outlined in green.
      </p>
      <p>
        When Publishing starts the first thing that happens is
        <br />
        1: We send the entire tree as stringified JSON to PUT
        /api/tree/[treeIdOfPreviousVersion]:[versionIdOfPreviousVersion] The
        result of this is a new tree with the same tree id as the previous
        version (the master tree id stays the same across versions) with a new
        version id
        <br />
        2: We then POST a publishTree query using the new tree id, version and
        version name. This adds an entry in the named_version table, this is how
        we identify published/master/bundled curricula.
      </p>
    </Box>
  )
}
